
import { Modal } from 'bootstrap';
import { defineComponent, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { Apollo, Notify } from '@/core/services';
import { ADD_ORDER_PRODUCT } from '../graphql/Mutations';
import { GET_SHOP_PRODUCTS } from '../graphql/Queries';
import InnerLoader from '../../../components/InnerLoader.vue';

export default defineComponent({
    name: 'AddOrderProducts',
    components: { InnerLoader },
    emits: ['added'],
    setup(_, { emit }) {
        const store = useStore();
        const i18n = useI18n();
        const loader = ref(false);
        const search = ref('');
        const appliedSearch = ref('');
        const products = ref<Array<Record<string, any>>>([]);
        const page = ref(1);
        const hasMore = ref(false);
        const customerId = ref<number | null>(null);
        const orderId = ref<number | null>(null);
        const addingId = ref<number | null>(null);
        const modal = ref<any>(null);
        const defaultImage = ref(store?.state?.AuthModule?.default_image);

        const productImage = (item) => {
            const media = item?.media || [];
            const featured = media.find((file) => file?.file_role === 'FTD') || media[0];
            return featured?.file_path || defaultImage.value;
        };

        const shopPrice = (item) => {
            if (item?.product_to_customer_prices?.length) {
                return item.product_to_customer_prices[0].special_price;
            }
            if (item?.product_to_price_groups?.length) {
                return item.product_to_price_groups[0].special_price;
            }
            return item?.tax_price_label;
        };

        const loadProducts = async (nextPage: number) => {
            if (!customerId.value) {
                products.value = [];
                return;
            }

            loader.value = true;
            try {
                const result = await Apollo.query({
                    query: GET_SHOP_PRODUCTS,
                    fetchPolicy: 'network-only',
                    variables: {
                        page: nextPage,
                        limit: 12,
                        order_customer_id: customerId.value,
                        search: appliedSearch.value || null
                    }
                });
                const listing = result?.data?.shop_products;
                const rows = listing?.data || [];
                products.value = nextPage === 1 ? rows : products.value.concat(rows);
                page.value = nextPage;
                hasMore.value = !!listing?.has_more_pages;
            } catch {
                // Apollo already shows the server message.
            } finally {
                loader.value = false;
            }
        };

        const searchProducts = () => {
            appliedSearch.value = search.value.trim();
            products.value = [];
            page.value = 1;
            hasMore.value = false;
            loadProducts(1);
        };

        const loadMore = () => {
            if (!loader.value && hasMore.value) {
                loadProducts(page.value + 1);
            }
        };

        const addProduct = async (item) => {
            if (!orderId.value || addingId.value) {
                return;
            }

            addingId.value = Number(item.id);
            try {
                await Apollo.mutate({
                    mutation: ADD_ORDER_PRODUCT,
                    variables: {
                        order_id: orderId.value,
                        product_id: Number(item.id),
                        quantity: 1
                    }
                });
                Notify.success(i18n.t('message.RECORD_UPDATED_SUCCESSFULLY'));
                emit('added');
            } catch {
                // Apollo already shows the server message.
            } finally {
                addingId.value = null;
            }
        };

        const show = (nextCustomerId: number, nextOrderId: number) => {
            customerId.value = Number(nextCustomerId) || null;
            orderId.value = Number(nextOrderId) || null;
            search.value = '';
            appliedSearch.value = '';
            products.value = [];
            hasMore.value = false;
            page.value = 1;
            addingId.value = null;
            const element = document.getElementById('order_shop_products_modal');
            if (!element || !customerId.value || !orderId.value) {
                return;
            }
            if (!modal.value) {
                modal.value = new Modal(element);
            }
            modal.value.show();
            loadProducts(1);
        };

        return {
            loader,
            search,
            products,
            searchProducts,
            hasMore,
            addingId,
            productImage,
            shopPrice,
            loadMore,
            addProduct,
            show
        };
    }
});
